import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { appRoutes } from '@/pages/page-registry';

export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const frame = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onOpenChange(false);
    }

    if (open) window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onOpenChange, open]);

  const results = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return appRoutes.slice(0, 10);

    return appRoutes
      .filter((route) => `${route.title} ${route.path} ${route.subtitle || ''}`.toLowerCase().includes(normalized))
      .slice(0, 10);
  }, [query]);

  if (!open) return null;

  return (
    <div className="command-backdrop" role="presentation" onMouseDown={() => onOpenChange(false)}>
      <div className="command-dialog" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
        <div className="command-input">
          <Search size={18} />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search pages"
          />
          <button type="button" aria-label="Close" onClick={() => onOpenChange(false)}>
            <X size={16} />
          </button>
        </div>
        <div className="command-results">
          {results.map((route) => {
            const Icon = route.icon;
            return (
              <button
                key={route.id}
                type="button"
                onClick={() => {
                  navigate(route.path.includes(':') ? route.path.replace(/:[^/]+/g, 'demo') : route.path);
                  onOpenChange(false);
                }}
              >
                <Icon size={17} />
                <span>
                  <strong>{route.title}</strong>
                  <small>{route.path}</small>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
